'use client';

import { useTradeContext } from '../../../../components/ui';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { LotteryOptionGoodsForm } from '../../types';
import styles from './OptionSelectedList.module.scss';

export type OptionSelectedListProps = {
  optionFormData: LotteryOptionGoodsForm[];
};

// 選択済みアイテムグループの型を定義
// `selectedItems` は「optionId -> 個数」のMap構造で、UIへの描画に必要な 「どの表示ラベル(staging)配下に、どの選択肢がいくつあるか」という情報を直接持っていない。
// そのため、描画前にこの `SelectedItemGroup[]` へ以下の観点で変換します。
// - 同じstagingKey(=同一表示グループ)の項目を集約する
// - UIが扱いやすい配列形式に正規化するための中間データとして作成する
type SelectedItemGroup = {
  labelName: string;
  stagingKey: string;
  stagingName: string;
  items: {
    name: string;
    count: number;
  }[];
};

const OptionSelectedList = ({ optionFormData }: OptionSelectedListProps) => {
  const { selectedItems } = useTradeContext();

  // stagingKeyで既存グループを探し、なければ新規グループを作って返す（配列への追加は呼び出し側で行う）
  const findOrBuildLabelGroup = (
    result: SelectedItemGroup[],
    label: LotteryOptionGoodsForm['label'],
  ): { labelGroup: SelectedItemGroup; shouldAppend: boolean } => {
    const existingGroup = result.find((group) => group.stagingKey === label.stagingKey);
    if (existingGroup) {
      // 既存の場合
      return {
        labelGroup: existingGroup,
        // 呼び出し元ではresultにpushしない
        shouldAppend: false,
      };
    }

    // 新規作成の場合
    return {
      labelGroup: {
        labelName: label.name,
        stagingKey: label.stagingKey,
        stagingName: label.stagingName,
        items: [],
      },
      // 呼び出し元ではresultにpushする
      shouldAppend: true,
    };
  };

  // `selectedItems`(Map) を UI描画用の `SelectedItemGroup[]` に変換する関数
  // 処理内容:
  // 1. countが0以下の未選択データを除外して、表示対象だけに絞る
  // 2. optionId から optionFormData 内の実体(selectable / namedSelectable)を特定する
  // 3. 同じ stagingKey ごとにグルーピングして、表示順を保ったまま items を積み上げる
  // 4. どの選択肢が何個選ばれたかを `items` に詰め、後続のUIへの描画処理を単純化する
  const createSelectedItemGroups = () => {
    const result: SelectedItemGroup[] = [];

    selectedItems.forEach((count, optionId) => {
      // 選択数が0個の場合は次のオプションへ(continue相当)
      if (count <= 0) return;
      // optionId は `${optionFormDataItem.id}-${index}` 形式なので、
      // optionFormData を走査して「どのフォーム項目の何番目の選択肢か」を逆引きする
      for (const optionFormDataItem of optionFormData) {
        const optionFormDataItemId = optionFormDataItem.id;
        const label = optionFormDataItem.label;
        const optionInfo = optionFormDataItem.optionInfo;

        // selectableの場合:
        // optionId と一致する index を見つけたら、対応するラベルグループへ集約する
        const selectableIndex = optionInfo.selectable.findIndex((_, index) => {
          return `${optionFormDataItemId}-${index}` === optionId;
        });
        if (selectableIndex !== -1) {
          const { labelGroup, shouldAppend } = findOrBuildLabelGroup(result, label);
          if (shouldAppend) {
            // グループがresultにないので、resultにグループをpushする
            result.push(labelGroup);
          }
          // labelGroup内のitemsにオプション名と選択数を詰め込む
          labelGroup.items.push({
            name: optionInfo.selectable[selectableIndex].label,
            count,
          });
          return;
        }

        // namedSelectableの場合:
        // selectable と同様に optionId から選択肢を特定し、同じ要領でグループへ追加する
        const namedSelectableIndex = optionInfo.namedSelectable.findIndex((_, index) => {
          return `${optionFormDataItemId}-${index}` === optionId;
        });
        if (namedSelectableIndex !== -1) {
          const { labelGroup, shouldAppend } = findOrBuildLabelGroup(result, label);
          if (shouldAppend) {
            // グループがresultにないので、resultにグループをpushする
            result.push(labelGroup);
          }
          // labelGroup内のitemsにオプション名と選択数を詰め込む
          labelGroup.items.push({
            name: optionInfo.namedSelectable[namedSelectableIndex].label,
            count,
          });
          return;
        }
      }
    });

    return result;
  };

  const selectedItemGroups = createSelectedItemGroups();

  // 選択された総数を計算
  const totalSelectedCount = selectedItemGroups.reduce((total, group) => {
    return total + group.items.reduce((groupTotal, item) => groupTotal + item.count, 0);
  }, 0);

  // 選択数が0個の場合は非表示
  if (totalSelectedCount === 0) {
    return null;
  }

  return (
    <div className={`${styles.selectedOptionContainer}`}>
      <div className={styles.optionSection}>
        {selectedItemGroups.map((group) =>
          group.items.map((item) => {
            return (
              <div key={item.name} className={styles.optionItem}>
                <div className={styles.optionDetails}>
                  <div className={styles.optionNameWrapper}>
                    <p className={styles.labelName}>{item.name}</p>
                  </div>
                  <div className={styles.selectedCountWrapper}>
                    <p className={styles.selectedCount}>
                      <span className={utilsStyles.textXLarge}>{item.count}</span>個選択済み
                    </p>
                  </div>
                </div>
              </div>
            );
          }),
        )}
      </div>
    </div>
  );
};

export default OptionSelectedList;
